import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getEvent } from "../../services/eventService";
import { createBooking } from "../../services/bookingService";
import { toggleFavorite } from "../../services/favoriteService";
import { createReview, deleteReview } from "../../services/reviewService";
import { useAuth } from "../../context/AuthContext";
import imgPlaceholder from "../../assets/images/resources/img-placeholder.svg";
import { EVENT_IMAGE_POOL, pickPoolImage } from "../../utils/imagePools";

const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const EventSingleMain = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    quantity: 1,
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    coupon_code: "",
  });
  const [bookingError, setBookingError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError(null);
    getEvent(slug)
      .then((res) => {
        setEvent(res.event);
        setReviews(res.event.reviews || []);
        setRelated(res.related_events || []);
        const firstAvailable = res.event.ticket_types?.find((t) => !t.sold_out);
        setSelectedTicket(firstAvailable?.id ?? res.event.ticket_types?.[0]?.id ?? null);
      })
      .catch(() => {
        setError("This event could not be found, or the Laravel API server is not running.");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handleBook = async (e) => {
    e.preventDefault();
    setBookingError("");

    if (!user) {
      navigate("/login", { state: { redirect: `/events/${slug}` } });
      return;
    }

    if (!selectedTicket) {
      setBookingError("একটা টিকেট টাইপ বেছে নিন।");
      return;
    }

    setSubmitting(true);
    try {
      const data = await createBooking(slug, { ...bookingForm, ticket_type_id: selectedTicket });
      navigate(`/payment/${data.booking_reference}`);
    } catch (err) {
      const errors = err?.response?.data?.errors;
      const firstError = errors ? Object.values(errors)[0]?.[0] : null;
      setBookingError(firstError || err?.response?.data?.message || "বুকিং করা যায়নি, আবার চেষ্টা করুন।");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFavorite = async () => {
    if (!user) {
      navigate("/login", { state: { redirect: `/events/${slug}` } });
      return;
    }
    const data = await toggleFavorite(event.id);
    setIsFavorite(data.status === "added");
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login", { state: { redirect: `/events/${slug}` } });
      return;
    }
    setReviewSubmitting(true);
    setReviewError("");
    try {
      const data = await createReview(event.id, reviewForm);
      setReviews((prev) => [data.review, ...prev]);
      setReviewForm({ rating: 5, comment: "" });
    } catch (err) {
      setReviewError(err?.response?.data?.message || "Review জমা দেওয়া যায়নি।");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleReviewDelete = async (reviewId) => {
    try {
      await deleteReview(reviewId);
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    } catch {
      setReviewError("Review মুছে ফেলা যায়নি।");
    }
  };

  if (loading) {
    return (
      <section className="event-details">
        <div className="container">
          <p>Loading event...</p>
        </div>
      </section>
    );
  }

  if (error || !event) {
    return (
      <section className="event-details">
        <div className="container">
          <p style={{ color: "#c0392b" }}>{error}</p>
          <Link to="/events" className="thm-btn">
            Back to Events
          </Link>
        </div>
      </section>
    );
  }

  return (
    <React.Fragment>
      {/* Event Details (Dynamic) Start */}
      <section className="event-details">
        <div className="container">
          <div className="row">
            <div className="col-xl-8 col-lg-7">
              <div className="event-details__img-1">
                <img
                  src={event.image || pickPoolImage(EVENT_IMAGE_POOL, event.id ?? event.slug ?? event.title) || imgPlaceholder}
                  alt={event.title}
                  style={{ width: "100%", borderRadius: "8px" }}
                  onError={(e) => { e.target.onerror = null; e.target.src = imgPlaceholder; }}
                />
              </div>

              <ul className="event-details__meta list-unstyled" style={{ marginTop: "20px" }}>
                <li>
                  <p>
                    <span className="icon-clock"></span> {formatDateTime(event.start_date)}
                  </p>
                </li>
                {event.venue?.name && (
                  <li>
                    <p>
                      <span className="icon-pin"></span> {event.venue.name}
                      {event.venue.city ? `, ${event.venue.city}` : ""}
                    </p>
                  </li>
                )}
                {event.category?.name && (
                  <li>
                    <p>
                      <span className="icon-tag"></span> {event.category.name}
                    </p>
                  </li>
                )}
              </ul>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 className="event-details__title-1">{event.title}</h3>
                <button
                  onClick={handleFavorite}
                  className="thm-btn"
                  style={{ background: isFavorite ? "#e6394f" : "#eee", color: isFavorite ? "#fff" : "#333" }}
                >
                  {isFavorite ? "♥ Favorited" : "♡ Favorite"}
                </button>
              </div>

              {event.description && (
                <p className="event-details__text-1" style={{ whiteSpace: "pre-line" }}>
                  {event.description}
                </p>
              )}

              {event.average_rating > 0 && (
                <p>
                  <strong>Rating:</strong> {event.average_rating} / 5 ({event.reviews_count} reviews)
                </p>
              )}

              {/* Related Events */}
              {related.length > 0 && (
                <div style={{ marginTop: "40px" }}>
                  <h3 className="event-details__title-1">Related Events</h3>
                  <div className="row">
                    {related.map((r) => (
                      <div className="col-xl-4 col-lg-4 col-md-6" key={r.id}>
                        <div className="blog-one__single">
                          <div className="blog-one__img">
                            <img
                              src={r.image || pickPoolImage(EVENT_IMAGE_POOL, r.id ?? r.slug ?? r.title) || imgPlaceholder}
                              alt={r.title}
                              onError={(e) => { e.target.onerror = null; e.target.src = imgPlaceholder; }}
                            />
                          </div>
                          <div className="blog-one__content">
                            <h3 className="blog-one__title">
                              <Link to={`/events/${r.slug}`}>{r.title}</Link>
                            </h3>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Reviews */}
              <div style={{ marginTop: "40px" }}>
                <h3 className="event-details__title-1">Reviews {event.reviews_count ? `(${event.reviews_count})` : ""}</h3>

                {reviews.length === 0 && <p>এখনো কোনো review নেই — প্রথম review আপনার হতে পারে।</p>}

                {reviews.map((r) => (
                  <div key={r.id} style={{ borderBottom: "1px solid #eee", padding: "14px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong>{r.user_name}</strong>
                      <span>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                    </div>
                    {r.comment && <p style={{ margin: "6px 0 0" }}>{r.comment}</p>}
                    {user?.id === r.user_id && (
                      <button
                        onClick={() => handleReviewDelete(r.id)}
                        style={{ background: "none", border: "none", color: "#e6394f", cursor: "pointer", padding: 0, fontSize: 13, marginTop: 4 }}
                      >
                        নিজের review মুছুন
                      </button>
                    )}
                  </div>
                ))}

                <form onSubmit={handleReviewSubmit} style={{ marginTop: 20, maxWidth: 420 }}>
                  <h4 style={{ fontSize: 16 }}>একটা review লিখুন</h4>
                  <p style={{ fontSize: 13, color: "#888" }}>
                    শুধু যাদের এই event এ confirmed booking আছে তারাই review দিতে পারবেন।
                  </p>
                  <div style={{ marginBottom: 10 }}>
                    <select
                      value={reviewForm.rating}
                      onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })}
                      style={{ padding: 8 }}
                    >
                      {[5, 4, 3, 2, 1].map((n) => (
                        <option key={n} value={n}>{"★".repeat(n)} ({n})</option>
                      ))}
                    </select>
                  </div>
                  <div className="contact-one__input-box">
                    <textarea
                      placeholder="মতামত (ঐচ্ছিক)"
                      rows={3}
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    />
                  </div>
                  {reviewError && <p style={{ color: "#e6394f" }}>{reviewError}</p>}
                  <button type="submit" className="thm-btn" disabled={reviewSubmitting}>
                    {reviewSubmitting ? "জমা হচ্ছে..." : user ? "Review জমা দিন" : "Login করে review দিন"}
                  </button>
                </form>
              </div>
            </div>

            {/* Ticket Booking Sidebar */}
            <div className="col-xl-4 col-lg-5">
              <div className="sidebar" style={{ border: "1px solid #eee", borderRadius: "8px", padding: "25px" }}>
                <h3 className="event-details__title-1" style={{ fontSize: "20px" }}>
                  Tickets
                </h3>

                {event.ticket_types && event.ticket_types.length > 0 ? (
                  <form onSubmit={handleBook}>
                    <ul className="list-unstyled">
                      {event.ticket_types.map((t) => (
                        <li
                          key={t.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 0",
                            borderBottom: "1px solid #eee",
                            opacity: t.sold_out ? 0.5 : 1,
                          }}
                        >
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: t.sold_out ? "not-allowed" : "pointer" }}>
                            <input
                              type="radio"
                              name="ticket_type"
                              disabled={t.sold_out}
                              checked={selectedTicket === t.id}
                              onChange={() => setSelectedTicket(t.id)}
                            />
                            {t.name}
                            {t.sold_out && <span style={{ color: "#c0392b" }}> (Sold out)</span>}
                          </label>
                          <strong>${t.price}</strong>
                        </li>
                      ))}
                    </ul>

                    <div className="contact-one__input-box" style={{ marginTop: 16 }}>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={bookingForm.quantity}
                        onChange={(e) => setBookingForm({ ...bookingForm, quantity: Number(e.target.value) })}
                        placeholder="Quantity"
                      />
                    </div>
                    <div className="contact-one__input-box">
                      <input
                        type="text"
                        placeholder="নাম"
                        value={bookingForm.name}
                        onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="contact-one__input-box">
                      <input
                        type="email"
                        placeholder="Email"
                        value={bookingForm.email}
                        onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="contact-one__input-box">
                      <input
                        type="text"
                        placeholder="ফোন (ঐচ্ছিক)"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                      />
                    </div>
                    <div className="contact-one__input-box">
                      <input
                        type="text"
                        placeholder="Coupon code (ঐচ্ছিক)"
                        value={bookingForm.coupon_code}
                        onChange={(e) => setBookingForm({ ...bookingForm, coupon_code: e.target.value })}
                      />
                    </div>

                    {bookingError && <p style={{ color: "#e6394f" }}>{bookingError}</p>}

                    <button type="submit" className="thm-btn" disabled={submitting} style={{ marginTop: "10px", width: "100%" }}>
                      {submitting ? "বুকিং হচ্ছে..." : user ? "Book Now" : "Book Now (লগইন লাগবে)"}
                    </button>
                  </form>
                ) : (
                  <p>Ticket information will be available soon.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Event Details End */}
    </React.Fragment>
  );
};

export default EventSingleMain;
